import { Box, styled, Typography } from '@mui/material';
import Loading from 'components/Loading';
import numbro from 'numbro';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { ORG_CHART_FINANCE_FIELD } from 'routes/BudgetModule/config/constants';
import { useGetSummary } from './hooks/hooks';
import BudgetFilters from 'routes/BudgetModule/components/shared/Filters/BudgetFilters';
import { getUpdatedFilters } from 'routes/BudgetModule/components/shared/Filters/helpers/filtersHelpers';
import { ALL_BUDGETS_VALUE } from 'routes/BudgetModule/components/BudgetOverview/constants';

type Props = {
    budgetProposals: BudgetProposalT[];
    selectedBudgetName: string | null | undefined;
    year: number | null | undefined;
};

const StyHeader = styled(Box, {
    name: 'StyHeader',
})({
    display: 'flex',
    alignItems: 'end',
    '& > div': {
        justifyContent: 'flex-end',
        margin: '0',
    },
});

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const StySectionTitle = styled(Typography, {
    name: 'StySectionTitle',
})(({ theme }) => ({
    paddingLeft: '16px',
    color: theme.palette.primary.main,
    minHeight: '48px',
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '4px 8px 24px rgba(0, 0, 0, 0.06)',
}));

const StySectionTitleSmall = styled(StySectionTitle, {
    name: 'StySectionTitle',
})({
    fontWeight: 'normal',
});

const getFormatedValue = (value?: number) =>
    value !== undefined
        ? numbro(value).formatCurrency({
              mantissa: 2,
              trimMantissa: true,
              spaceSeparated: true,
              thousandSeparated: true,
          })
        : value;

/**
 * Retrieves the target budget proposals based on the selected budget name and year.
 * If the selected budget name is 'TOUS LES BUDGETS', filter on year only.
 */
const getTargetBudgetProposals = (
    budgetProposals: BudgetProposalT[],
    selectedBudgetName: string | null | undefined,
    year: number | null | undefined,
): BudgetProposalT[] =>
    budgetProposals.filter((bp: BudgetProposalT) =>
        selectedBudgetName === ALL_BUDGETS_VALUE
            ? bp.exercice === year
            : bp.budgetName === selectedBudgetName && bp.exercice === year,
    );

export default function FinanceRatios(props: Props): React.ReactElement {
    const { budgetProposals, selectedBudgetName, year } = props;
    const targetBudgetProposals = getTargetBudgetProposals(
        budgetProposals,
        selectedBudgetName,
        year,
    );

    const { t } = useTranslation();

    const OrgChartFinanceFilter: SentenceFilterT = {
        displayIndex: 0,
        field: ORG_CHART_FINANCE_FIELD,
        operation: 'in_',
        type: 'category',
        value: null,
        valueScale: null,
    };
    const [filters, setFilters] = useState<SentenceFilterT[]>([
        OrgChartFinanceFilter,
    ]);

    const updateBudgetSynthesisFilters = (
        newFilterData: Partial<SentenceFilterT>,
    ): void => {
        setFilters(getUpdatedFilters(filters, newFilterData));
    };

    const valueOrgChartFinanceFilter =
        filters.find((filter) => filter.field === ORG_CHART_FINANCE_FIELD)
            ?.value || '';

    // management of selected BP for budgetOverview is done in upper scope so no need to deal with targetBudgetProposals undefined
    const { data: summary, isLoading } = useGetSummary(
        targetBudgetProposals,
        valueOrgChartFinanceFilter,
    );
    if (isLoading) return <Loading />;

    return (
        <Box sx={{ padding: '3rem' }}>
            <StyHeader>
                <StyTitle variant="h5">
                    {t('budgetOverview.budgetBalance')}
                </StyTitle>
                <BudgetFilters
                    filters={filters}
                    updateFilter={updateBudgetSynthesisFilters}
                />
            </StyHeader>
            <StySectionTitleSmall variant="body1Bold">
                {`${t('budgetBalance.reIncome')} : ${getFormatedValue(
                    summary?.reAmount,
                )}`}
            </StySectionTitleSmall>
            <StySectionTitleSmall variant="body1Bold">
                {`${t('budgetBalance.cpExpense')} : ${getFormatedValue(
                    summary?.cpAmount,
                )}`}
            </StySectionTitleSmall>
            <StySectionTitle variant="body1Bold">
                {`${t('budgetBalance.totalCPRE')} : ${getFormatedValue(
                    summary?.total,
                )}`}
            </StySectionTitle>
        </Box>
    );
}
